import type { RoleId } from "./roles";

export interface QuestionOption {
  id: string;
  text: string;
  primary: RoleId;
  secondary: RoleId;
}

export interface Question {
  id: number;
  category: "social" | "support" | "planning" | "online" | "aesthetic" | "travel";
  prompt: string;
  options: readonly QuestionOption[];
}

export const QUESTIONS: readonly Question[] = [
  {
    id: 1,
    category: "social",
    prompt: "大家第一次见面，空气突然安静下来，TA 更可能：",
    options: [
      { id: "1a", text: "抛出一个大家都容易接住的话题", primary: "spark", secondary: "care" },
      { id: "1b", text: "先和旁边最拘谨的人轻轻聊两句", primary: "care", secondary: "warmth" },
      { id: "1c", text: "想一个简单的小互动让大家熟起来", primary: "idea", secondary: "spark" },
      { id: "1d", text: "先观察气氛，再自然加入合适的话题", primary: "roam", secondary: "steady" },
    ],
  },
  {
    id: 2,
    category: "social",
    prompt: "朋友过生日，TA 最可能为这一天加上的一笔是：",
    options: [
      { id: "2a", text: "让全场都能笑起来的一段开场", primary: "spark", secondary: "warmth" },
      { id: "2b", text: "把时间、地点和关键环节安排妥当", primary: "steady", secondary: "care" },
      { id: "2c", text: "写下一段只属于这个朋友的真心话", primary: "warmth", secondary: "idea" },
      { id: "2d", text: "记住朋友的偏好，准备一个合适的小惊喜", primary: "care", secondary: "idea" },
    ],
  },
  {
    id: 3,
    category: "support",
    prompt: "朋友说“今天有点撑不住”，TA 通常会：",
    options: [
      { id: "3a", text: "先听完，再问对方此刻最需要什么", primary: "care", secondary: "warmth" },
      { id: "3b", text: "用一句很贴近对方的话接住情绪", primary: "warmth", secondary: "idea" },
      { id: "3c", text: "记下让对方为难的细节，之后再跟进", primary: "care", secondary: "steady" },
      { id: "3d", text: "提议换个环境，陪对方喘一口气", primary: "idea", secondary: "spark" },
    ],
  },
  {
    id: 4,
    category: "support",
    prompt: "一群朋友因为意见不同卡住了，TA 更可能：",
    options: [
      { id: "4a", text: "先承认每个人的感受，再找下一步", primary: "warmth", secondary: "steady" },
      { id: "4b", text: "整理大家的共识，留下可调整的空间", primary: "steady", secondary: "roam" },
      { id: "4c", text: "照顾各方在意的点，也让气氛松一点", primary: "care", secondary: "spark" },
      { id: "4d", text: "让大家缓一缓，再带回一个新角度", primary: "roam", secondary: "idea" },
    ],
  },
  {
    id: 5,
    category: "planning",
    prompt: "大家临时想约一个周末，TA 会怎样推动它发生？",
    options: [
      { id: "5a", text: "先列出两三个可行方案让大家选", primary: "steady", secondary: "idea" },
      { id: "5b", text: "找到一个既新鲜又照顾大家习惯的点子", primary: "idea", secondary: "care" },
      { id: "5c", text: "先把最期待的部分定下，其他边走边看", primary: "spark", secondary: "roam" },
      { id: "5d", text: "逐个确认大家最在意的时间和节奏", primary: "warmth", secondary: "care" },
    ],
  },
  {
    id: 6,
    category: "planning",
    prompt: "一件大家都想做的事突然只剩半天准备，TA 会：",
    options: [
      { id: "6a", text: "抓住必须完成的部分，给变化留余地", primary: "steady", secondary: "roam" },
      { id: "6b", text: "分好任务，也留意有没有人压力太大", primary: "steady", secondary: "warmth" },
      { id: "6c", text: "先点燃大家的行动感，再补齐细节", primary: "spark", secondary: "idea" },
      { id: "6d", text: "从最顺手的入口开始，边做边校准", primary: "roam", secondary: "care" },
    ],
  },
  {
    id: 7,
    category: "online",
    prompt: "群聊安静了好几天，TA 发来的内容更像：",
    options: [
      { id: "7a", text: "一个让大家立刻想接话的小话题", primary: "spark", secondary: "idea" },
      { id: "7b", text: "一句自然的问候，顺便关心近况", primary: "warmth", secondary: "spark" },
      { id: "7c", text: "一条有用信息，再附上清楚的重点", primary: "steady", secondary: "idea" },
      { id: "7d", text: "一件刚发现的有趣事，邀请大家一起看", primary: "idea", secondary: "roam" },
    ],
  },
  {
    id: 8,
    category: "aesthetic",
    prompt: "一起留下照片和记录时，TA 更在意：",
    options: [
      { id: "8a", text: "把大家真正喜欢的瞬间串成一个主题", primary: "idea", secondary: "warmth" },
      { id: "8b", text: "记录每个人最自然、最放松的样子", primary: "warmth", secondary: "roam" },
      { id: "8c", text: "让成片有活力，也把拍摄节奏照顾好", primary: "spark", secondary: "steady" },
      { id: "8d", text: "不追求统一，保留当时偶然的惊喜", primary: "roam", secondary: "warmth" },
    ],
  },
  {
    id: 9,
    category: "travel",
    prompt: "旅途中计划突然被天气打乱，TA 第一反应是：",
    options: [
      { id: "9a", text: "把意外变成新节目，先稳住大家兴致", primary: "roam", secondary: "spark" },
      { id: "9b", text: "快速确认条件，找一个能马上执行的替代", primary: "steady", secondary: "spark" },
      { id: "9c", text: "先问大家累不累，再选择舒服的方案", primary: "care", secondary: "roam" },
      { id: "9d", text: "从限制里想出一条没走过的新路线", primary: "idea", secondary: "steady" },
    ],
  },
  {
    id: 10,
    category: "travel",
    prompt: "旅行里忽然多出一个完全自由的下午，TA 会：",
    options: [
      { id: "10a", text: "提议一个大家都会期待的即兴小目标", primary: "spark", secondary: "roam" },
      { id: "10b", text: "选好一个方向，沿途看到什么再决定", primary: "roam", secondary: "steady" },
      { id: "10c", text: "先照顾每个人的体力，再决定去哪儿", primary: "care", secondary: "roam" },
      { id: "10d", text: "陪大家慢慢逛，把舒服的节奏留住", primary: "warmth", secondary: "care" },
    ],
  },
] as const;

export const QUIZ_LENGTH = QUESTIONS.length;

export const QUESTION_BY_ID = new Map(
  QUESTIONS.map((question) => [question.id, question]),
);

export function findOption(questionId: number, optionId: string): QuestionOption | undefined {
  return QUESTION_BY_ID.get(questionId)?.options.find((option) => option.id === optionId);
}
